import React from "react";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, Mail, Clock, Shield, HelpCircle, 
  CheckCircle, XCircle, FileText, CreditCard, 
  Settings, Database, AlertCircle
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

/**
 * CLARIAS Customer Support Page
 * Implements CLARIAS-CUSTOMER-SUPPORT-GOVERNANCE requirements
 */
const SupportPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Dashboard</span>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-indigo-100 mb-6">
            <HelpCircle className="w-8 h-8 text-indigo-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-3">Customer Support</h1>
          <p className="text-slate-500">We're here to help with your CLARIAS account</p>
        </div>

        <div className="space-y-6">
          
          {/* Primary Contact Card */}
          <Card className="border-0 shadow-md bg-gradient-to-br from-indigo-500 to-purple-600 text-white overflow-hidden">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center shrink-0">
                  <Mail className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold mb-1">Customer Support & Grievance Contact</h2>
                  <a 
                    href="mailto:support@clarias.in" 
                    className="text-2xl font-semibold text-white hover:text-indigo-100 transition-colors"
                  >
                    support@clarias.in
                  </a>
                  <p className="text-indigo-100 text-sm mt-3">
                    This email also serves as the Grievance Officer contact under applicable Indian laws.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Response Time */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                  <Clock className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Response Time</h3>
                  <div className="space-y-2 text-sm text-slate-600">
                    <p className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      <span><strong>Auto-acknowledgement:</strong> Immediate</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      <span><strong>Human review & resolution:</strong> 24–72 business hours</span>
                    </p>
                  </div>
                  <p className="mt-3 text-sm text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
                    Typical response time: 24–48 business hours.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* What We Can Help With */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-indigo-600" />
                What We Can Help With
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                {/* Covered */}
                <div className="space-y-3">
                  <p className="text-sm font-medium text-emerald-700 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" /> Support Covers:
                  </p>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start gap-2">
                      <Settings className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                      <span>Account access issues</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CreditCard className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                      <span>Billing and subscription queries</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                      <span>Technical issues</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Database className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                      <span>Data protection requests</span>
                    </li>
                  </ul>
                </div>
                
                {/* Not Covered */}
                <div className="space-y-3">
                  <p className="text-sm font-medium text-red-600 flex items-center gap-2">
                    <XCircle className="w-4 h-4" /> Not Covered:
                  </p>
                  <ul className="space-y-2 text-sm text-slate-500">
                    <li className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" />
                      <span>Exam strategy discussions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" />
                      <span>Personal mentoring</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" />
                      <span>Rank prediction explanations</span>
                    </li>
                  </ul>
                  <p className="text-xs text-slate-400 mt-2">
                    For exam preparation guidance, please use the Clarity AI assistant within the app.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Data Deletion Requests */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Database className="w-5 h-5 text-amber-600" />
                Data & Account Deletion
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                Under the Digital Personal Data Protection Act, 2023, you have the right to request 
                deletion of your personal data and account.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-sm text-amber-800 font-medium mb-2">To request data deletion:</p>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>1. Go to <Link to="/account-settings" className="text-amber-600 underline font-medium">Account Settings</Link> and click "Delete My Account"</li>
                  <li>2. Or email <a href="mailto:support@clarias.in" className="text-amber-600 underline font-medium">support@clarias.in</a> with your request</li>
                </ul>
              </div>
              <p className="text-xs text-slate-500 mt-4">
                We will process your request as per applicable data protection laws.
              </p>
            </CardContent>
          </Card>

          {/* Legal Links */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-600" />
                Legal Documents
              </h3>
              <div className="space-y-2">
                <Link 
                  to="/terms-of-service"
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <span className="text-sm text-slate-700">Terms of Service</span>
                  <ArrowLeft className="w-4 h-4 text-slate-400 rotate-180" />
                </Link>
                <Link 
                  to="/privacy-policy"
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <span className="text-sm text-slate-700">Privacy Policy</span>
                  <ArrowLeft className="w-4 h-4 text-slate-400 rotate-180" />
                </Link>
                <Link 
                  to="/refund-policy"
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <span className="text-sm text-slate-700">Refund & Cancellation Policy</span>
                  <ArrowLeft className="w-4 h-4 text-slate-400 rotate-180" />
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Important Notice */}
          <div className="bg-slate-100 rounded-xl p-4 text-center">
            <p className="text-xs text-slate-500">
              CLARIAS provides AI-based educational assistance. It does not guarantee selection, rank, marks, or exam results.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SupportPage;
